import {
  IconButton,
  Modal,
  SingleSelectDropdown,
  TableRow,
  type DataTableColumn,
} from "@carbon6/c6-frontend-design";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { TrainingEvalEpisodeView } from "@/api/trainingEval";
import { hatcheryDropdownModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  episodeTimeRows,
  formatEpisodeSeconds,
  summarizeEpisodeTimes,
  type EpisodeTime,
} from "./episodeTimeModel";
import type { EvalRunDetailController } from "./useEvalRunDetailController";
import styles from "./EpisodeTimesModal.module.css";

type EpisodeTimesProps = {
  runId: string;
  termsState: EvalRunDetailController["termsState"];
  onRetry: () => void;
};

export function EpisodeTimesButton(props: EpisodeTimesProps): ReactNode {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  // 중첩 Modal은 닫힐 때 부모 dialog로 포커스를 돌린다. 이 진입 버튼으로 다시 좁힌다.
  useEffect(() => {
    if (!open && restoreFocus.current) {
      restoreFocus.current = false;
      trigger.current?.focus();
    }
  }, [open]);
  function close(): void {
    restoreFocus.current = true;
    setOpen(false);
  }
  return (
    <>
      <IconButton
        ref={trigger}
        text="Episode 시간"
        size="sm"
        variant="surface"
        onClick={() => setOpen(true)}
      />
      {open ? <EpisodeTimesModal {...props} onClose={close} /> : null}
    </>
  );
}

function EpisodeTimesModal({
  runId,
  termsState,
  onRetry,
  onClose,
}: EpisodeTimesProps & { onClose: () => void }): ReactNode {
  const titleId = useId();
  return (
    <Modal open onClose={onClose} size="lg" level="top" maxHeight="80dvh" aria-labelledby={titleId}>
      <HatModalHeader title="Episode 경과 시간" titleId={titleId} close />
      <div className={styles.intro}>
        <span className={styles.runId}>{runId}</span>
        <span>
          Episode가 종료된 시점의 sim 시간입니다. 실행 소요 시간이나 영상 길이와 다릅니다.
        </span>
      </div>
      {termsState.status === "success" ? (
        <EpisodeTimesTable episodes={termsState.data.episodes} />
      ) : (
        <Modal.Body>
          {termsState.status === "error" ? (
            <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
              Episode 시간을 불러오지 못했습니다. {termsState.message.slice(0, 180)}
            </EmptyState>
          ) : (
            <LoadingState size="sm">Episode 시간을 불러오고 있습니다.</LoadingState>
          )}
        </Modal.Body>
      )}
    </Modal>
  );
}

const TIME_NOTES: Record<NonNullable<EpisodeTime["reason"]>, string> = {
  after_reset: "auto-reset으로 측정 불가",
  missing: "미기록",
  invalid: "유효하지 않은 값",
};

class EpisodeTimeTableRow extends TableRow {
  readonly envId: string;
  readonly episodeIndex: string;
  readonly outcome: string;
  readonly elapsed: string;
  readonly note: string;

  constructor(private readonly time: EpisodeTime) {
    super();
    this.envId = time.envId === null ? "—" : String(time.envId);
    this.episodeIndex = time.episodeIndex === null ? "—" : String(time.episodeIndex);
    this.outcome = time.outcome;
    this.elapsed = formatEpisodeSeconds(time.seconds);
    this.note = time.reason === null ? "" : TIME_NOTES[time.reason];
  }
  getRowId(): string {
    return String(this.time.index);
  }
  canShow(): boolean {
    return true;
  }
  canSelect(): boolean {
    return false;
  }
}

const COLUMNS: readonly DataTableColumn<EpisodeTimeTableRow>[] = [
  { key: "envId", header: "Env", width: "65px" },
  { key: "episodeIndex", header: "Episode", width: "85px" },
  { key: "outcome", header: "결과", width: "110px" },
  {
    key: "elapsed",
    header: "경과 시간 (s)",
    width: "120px",
    align: "right",
    className: styles.number,
  },
  { key: "note", header: "비고", width: "minmax(180px, 1fr)" },
];

const ALL_RESULTS = "all";

function EpisodeTimesTable({
  episodes,
}: {
  episodes: readonly TrainingEvalEpisodeView[];
}): ReactNode {
  const [result, setResult] = useState(ALL_RESULTS);
  const rows = useMemo(() => episodeTimeRows(episodes), [episodes]);
  const options = useMemo(
    () => [
      { value: ALL_RESULTS, label: "전체" },
      ...Array.from(new Set(rows.map((row) => row.outcome)), (outcome) => ({
        value: `outcome:${outcome}`,
        label: outcome,
      })),
    ],
    [rows],
  );
  const visible = useMemo(
    () =>
      result === ALL_RESULTS ? rows : rows.filter((row) => result === `outcome:${row.outcome}`),
    [rows, result],
  );
  const source = useMemo(
    () => new StaticTableSource(visible.map((row) => new EpisodeTimeTableRow(row))),
    [visible],
  );
  useEffect(() => () => source.dispose(), [source]);
  const stats = summarizeEpisodeTimes(visible);
  const resultLabel = options.find((option) => option.value === result)?.label ?? "전체";

  return (
    <div className={styles.content}>
      <div className={styles.filters}>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="결과"
          value={result}
          neutralValue={ALL_RESULTS}
          options={options}
          onValueChange={(value) => setResult(value ?? ALL_RESULTS)}
        />
      </div>
      <div className={styles.table}>
        <HatDataTable
          source={source}
          columns={COLUMNS}
          ariaLabel="Episode 경과 시간"
          emptyMessage={
            episodes.length === 0
              ? "이 실행에 기록된 Episode가 없습니다."
              : "선택한 결과에 해당하는 Episode가 없습니다."
          }
          showIndex={false}
          scrollable
          minWidth="560px"
        />
      </div>
      <div className={styles.summary} role="status" aria-label="Episode 시간 통계">
        <span>{resultLabel} Episode 종료 시간</span>
        <div className={styles.stats}>
          {(
            [
              ["평균", stats.mean],
              ["최솟값", stats.min],
              ["최댓값", stats.max],
            ] as const
          ).map(([label, value]) => (
            <span key={label}>
              {label}{" "}
              <strong>
                {formatEpisodeSeconds(value)}
                {value === null ? "" : " s"}
              </strong>
            </span>
          ))}
        </div>
        <span className={styles.muted}>
          계산 대상 {stats.measured}/{stats.total}개 · 측정 불가 {stats.afterReset}개 · 미기록{" "}
          {stats.missing}개{stats.invalid > 0 ? ` · 유효하지 않은 값 ${stats.invalid}개` : ""}
        </span>
      </div>
    </div>
  );
}
